import { OrgServers } from "../../../../state/types";

import { getFieldValue } from "./get-field-value";
import { getProductIcon } from "./get-product-icon";

type MoveTicket = {
    id: string;
    status: {
        category: string;
        text: string;
    };
    summary: string;
    description: string;
    atlassianCloudUrl: string | null;
    planToMigrate: string | null;
    destinationLicense: string | null;
    sourceLicense: string | null;
    migrationProducts: string | null;
    migrationPhase: string | null;
};

// This is a temporary function to extract the product value from the migrationProducts string
// TODO: Remove this when SAC team have implemented mapping on the backend
export const extractValueFromUnmappedField = (
    field: string | null
): string | null => {
    if (!field) {
        return null;
    }

    const match = /value=([^,]+)/.exec(field);
    const value = match && match[1] ? match[1] : null;

    if (value === "None Selected") {
        return null;
    }

    return value;
};

type TableRow = {
    id: string;
    productType: string;
    sourceUrls: string;
    destinationUrl: string;
    phase: string;
    status: string;
};

const getSortedTableRows = (
    rows: TableRow[],
    sortKey: string | null,
    sortDirection: "asc" | "desc"
): TableRow[] => {
    return rows.sort((a, b) => {
        const getValue = (row: TableRow, key: string | null): string => {
            switch (key) {
                case "source":
                    return row.sourceUrls;
                case "destination":
                    return row.destinationUrl;
                case "phase":
                    return row.phase;
                case "status":
                    return row.status;
                case "ticket":
                    return row.id;
                default:
                    return "";
            }
        };

        if (!sortKey) return 0;

        const aValue = getValue(a, sortKey);
        const bValue = getValue(b, sortKey);

        return sortDirection === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
    });
};

interface GetTableRowsReturn {
    rows: TableRow[];
    rowsAnalyticsInfo: {
        // analytics on missing information in the table display
        rowsMissingDestinationUrl: number; // tickets without destination url custom field
        rowsMissingProductIcon: number; // tickets with empty product icon
        rowsMissingServerDetails: number; // tickets without server sen-id custom field
        serversMissingBaseUrl: number; // no. unique servers that could not be matched to a url
    };
}

export const getTableRows = ({
    moveTickets,
    orgServers,
    sortKey,
    sortDirection
}: {
    moveTickets: MoveTicket[];
    orgServers: OrgServers;
    sortKey: string | null;
    sortDirection: "asc" | "desc";
}): GetTableRowsReturn => {
    let rowsMissingDestinationUrl = 0;
    let rowsMissingProductIcon = 0;
    let rowsMissingServerDetails = 0;
    const allTicketServerSenIds: string[] = [];
    const allMMAServerSenIds: string[] = orgServers.data.map(
        (server) => server.supportEntitlementNumber
    );

    const mappedRows = moveTickets.map(
        ({
            id,
            destinationLicense,
            migrationProducts,
            migrationPhase,
            atlassianCloudUrl,
            sourceLicense,
            status
        }) => {
            const senIds = sourceLicense
                ?.split(",")
                .map((senId) => senId.trim());

            const mappedSenUrls = senIds?.map((senId) => {
                const mappedSenUrl =
                    orgServers.data.find(
                        (server) => server.supportEntitlementNumber === senId
                    )?.serverBaseUrl ?? senId;
                return mappedSenUrl;
            });
            const mappedProduct =
                extractValueFromUnmappedField(migrationProducts);

            // increment analytics counts
            if (!atlassianCloudUrl && !destinationLicense) {
                rowsMissingDestinationUrl += 1;
            }
            if (!senIds) {
                rowsMissingServerDetails += 1;
            }
            if (
                !mappedProduct ||
                !getProductIcon({ productType: mappedProduct, type: "source" })
            ) {
                rowsMissingProductIcon += 1;
            }
            allTicketServerSenIds.push(...(senIds ?? []));

            return {
                id,
                productType: getFieldValue({
                    label: "Migration Products",
                    value: mappedProduct
                }),
                sourceUrls: mappedSenUrls?.join(", ") ?? "",
                destinationUrl: getFieldValue({
                    label: "Atlassian Cloud URL",
                    value: atlassianCloudUrl ?? destinationLicense
                }),
                phase: getFieldValue({
                    label: "Migration Phase",
                    value: extractValueFromUnmappedField(migrationPhase)
                }),
                status: status.text
            };
        }
    );
    const allUniqueTicketServerSenIds = [...new Set(allTicketServerSenIds)];
    const serversMissingBaseUrl = allUniqueTicketServerSenIds.filter(
        (mmaSenId) => !allMMAServerSenIds.includes(mmaSenId)
    ).length;

    return {
        rows: getSortedTableRows(mappedRows, sortKey, sortDirection),
        rowsAnalyticsInfo: {
            rowsMissingDestinationUrl,
            rowsMissingProductIcon,
            rowsMissingServerDetails,
            serversMissingBaseUrl
        }
    };
};
